/**
 * LiveCanvasPanel — docked right-side panel that mirrors the AI's
 * latest `render_inline_panel` push.
 *
 * The user's AI client (over MCP) is the control surface; the canvas is
 * the display. When the AI calls render_inline_panel, the MCP tool writes the spec to
 * workspaces.live_canvas and broadcasts a `workspace_live_canvas`
 * BrowserBus event. The query key ["live-canvas", workspace_id]
 * invalidates, this component refetches, and the new panel renders
 * via PanelRenderer — all within seconds, no refresh required.
 *
 * Lives across every routed page (mounted at the Layout level). The
 * user can collapse it (slide-out) or dismiss the current canvas
 * (DELETE /workspaces/:id/live-canvas).
 */
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Maximize2, Minimize2, PanelRightClose, Sparkles, X } from "lucide-react";
import { PanelRenderer } from "../../framework/react/PanelRenderer";
import { WIDGET_REGISTRY } from "../widgets/registry";
import { getCsrfHeader } from "../lib/csrf";

interface LiveCanvas {
  spec: unknown;
  title: string | null;
  updated_at: string;
  updated_by_user_id: string | null;
  source_token: string | null;
}

interface LiveCanvasResponse {
  live_canvas: LiveCanvas | null;
}

async function fetchLiveCanvas(workspaceId: string): Promise<LiveCanvasResponse> {
  const res = await fetch(
    `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/live-canvas`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`live-canvas ${res.status}`);
  return res.json();
}

async function clearLiveCanvas(workspaceId: string): Promise<void> {
  const headers: Record<string, string> = { ...getCsrfHeader() };
  const res = await fetch(
    `/api/v1/workspaces/${encodeURIComponent(workspaceId)}/live-canvas`,
    { method: "DELETE", credentials: "include", headers },
  );
  if (!res.ok) throw new Error(`clear live-canvas ${res.status}`);
}

const COLLAPSED_KEY = "cmcp:live-canvas-collapsed";
const WIDE_KEY = "cmcp:live-canvas-wide";
const resolveWorkspaceWidget = (id: string) => WIDGET_REGISTRY[id] ?? null;

interface Props {
  workspaceId: string | null;
}

export function LiveCanvasPanel({ workspaceId }: Props) {
  const qc = useQueryClient();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(COLLAPSED_KEY) === "1";
  });
  const [wide, setWide] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem(WIDE_KEY) === "1";
  });

  const { data } = useQuery({
    queryKey: ["live-canvas", workspaceId ?? ""],
    queryFn: () => fetchLiveCanvas(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 10_000,
  });

  const clearMutation = useMutation({
    mutationFn: () => clearLiveCanvas(workspaceId!),
    onSuccess: () => {
      qc.setQueryData(["live-canvas", workspaceId ?? ""], { live_canvas: null });
    },
  });

  // When a fresh canvas arrives, auto-expand so the user actually sees
  // what the AI pushed. The user can still collapse afterward.
  const lastUpdate = data?.live_canvas?.updated_at ?? null;
  useEffect(() => {
    if (lastUpdate) {
      setCollapsed(false);
      sessionStorage.removeItem(COLLAPSED_KEY);
    }
  }, [lastUpdate]);

  // The inline-panel chip fires `cmcp:expand-live-canvas` when the user
  // clicks "Show →". Listen and uncollapse so the panel pops back open.
  useEffect(() => {
    const onExpand = () => {
      setCollapsed(false);
      sessionStorage.removeItem(COLLAPSED_KEY);
    };
    window.addEventListener("cmcp:expand-live-canvas", onExpand);
    return () => window.removeEventListener("cmcp:expand-live-canvas", onExpand);
  }, []);

  if (!workspaceId) return null;
  const canvas = data?.live_canvas ?? null;
  if (!canvas) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => {
          setCollapsed(false);
          sessionStorage.removeItem(COLLAPSED_KEY);
        }}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-ink hover:bg-action text-white text-[11px] uppercase tracking-wider font-semibold px-2 py-3 rounded-l-md shadow-[var(--shadow-overlay)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        style={{ writingMode: "vertical-rl" } as React.CSSProperties}
        title={canvas.title ?? "Live Canvas"}
      >
        Live Canvas
      </button>
    );
  }

  // bottom-24 clears the bottom live-session rail on narrower screens.
  // On wide screens it just leaves a small footer gap.
  const widthClass = wide ? "w-[min(780px,calc(100vw-1.5rem))]" : "w-[min(440px,calc(100vw-1.5rem))]";
  return (
    <aside aria-label="Live Canvas" className={`fixed right-3 top-16 bottom-24 ${widthClass} z-40 bg-white border border-stone-200 rounded-xl shadow-[var(--shadow-overlay)] flex flex-col overflow-hidden transition-[width] duration-200`}>
      <header className="flex items-center gap-2 px-3 py-2 border-b border-stone-100 bg-stone-50">
        <div className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] uppercase tracking-wider text-ink font-semibold">
          <Sparkles size={10} aria-hidden="true" /> Live Canvas
        </div>
        <div className="flex-1 text-sm font-semibold text-ink truncate" title={canvas.title ?? undefined}>
          {canvas.title ?? "AI render"}
        </div>
        <button
          type="button"
          onClick={() => {
            setWide((prev) => {
              const next = !prev;
              try { sessionStorage.setItem(WIDE_KEY, next ? "1" : "0"); } catch {}
              return next;
            });
          }}
          className="grid h-7 w-7 place-items-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-ink"
          title={wide ? "Standard width" : "Expand canvas width"}
          aria-label={wide ? "Standard width" : "Expand canvas width"}
        >
          {wide ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
        </button>
        <button
          type="button"
          onClick={() => {
            setCollapsed(true);
            sessionStorage.setItem(COLLAPSED_KEY, "1");
          }}
          className="grid h-7 w-7 place-items-center rounded-md text-stone-500 hover:bg-stone-100 hover:text-ink"
          title="Collapse"
          aria-label="Collapse live canvas"
        >
          <PanelRightClose size={15} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => clearMutation.mutate()}
          disabled={clearMutation.isPending}
          className="grid h-7 w-7 place-items-center rounded-md text-stone-500 hover:bg-danger-soft hover:text-danger disabled:opacity-50"
          title="Dismiss canvas"
          aria-label="Dismiss canvas"
        >
          <X size={15} aria-hidden="true" />
        </button>
      </header>
      <div className="flex-1 overflow-auto p-3">
        <PanelRenderer spec={canvas.spec as never} workspaceId={workspaceId} resolveWidget={resolveWorkspaceWidget} />
      </div>
      <footer className="px-3 py-1.5 border-t border-stone-100 bg-stone-50 text-[11px] text-stone-500 flex items-center justify-between">
        <span>Updated {new Date(canvas.updated_at).toLocaleTimeString()}</span>
        <span>AI-rendered · live</span>
      </footer>
    </aside>
  );
}
